import React, { FC, useState, useRef, useEffect, useMemo, useCallback } from 'react';
import styles from './Captions.module.scss';
import {
  saveMasterTemplateSettings,
  loadMasterTemplateSettings,
} from '@/components/CaptionSettingsPanel';
import highlightData from './sample_demo_highlight_caption.json';

// Import demo assets
// Served from public/ (not webpack-hashed) so the binary always deploys and
// isn't swallowed by the SPA rewrite under /static/media.
const sampleVideo = `${process.env.PUBLIC_URL}/sample_demo_video.mp4`;

type OptionMode = 'default' | 'customize';
type AnimationSpeed = '0.5x' | '1x' | '2x' | 'Instant';
type FontOption =
  | 'Montserrat'
  | 'Open Sans Light'
  | 'Open Sans Regular'
  | 'Open Sans Medium'
  | 'Open Sans SemiBold'
  | 'Open Sans Bold'
  | 'Open Sans ExtraBold';
type BorderWidth = '4px' | '6px' | '8px';

interface WordItem {
  start: number;
  end: number;
  text: string;
  type: string;
  alternatives: { confidence: number; content: string }[];
}

interface HighlightData {
  transcript: string;
  items: WordItem[];
}

// Map font option names to actual CSS font-family values
const fontFamilyMap: Record<FontOption, string> = {
  Montserrat: "'Montserrat', sans-serif",
  'Open Sans Light': "'Open Sans', sans-serif",
  'Open Sans Regular': "'Open Sans', sans-serif",
  'Open Sans Medium': "'Open Sans', sans-serif",
  'Open Sans SemiBold': "'Open Sans', sans-serif",
  'Open Sans Bold': "'Open Sans', sans-serif",
  'Open Sans ExtraBold': "'Open Sans', sans-serif",
};

const fontWeightMap: Record<FontOption, number> = {
  Montserrat: 400,
  'Open Sans Light': 300,
  'Open Sans Regular': 400,
  'Open Sans Medium': 500,
  'Open Sans SemiBold': 600,
  'Open Sans Bold': 700,
  'Open Sans ExtraBold': 800,
};

// Reference width for scaling (standard 1080p video width)
// Caption sizes are designed to look correct at this reference width
const REFERENCE_VIDEO_WIDTH = 1920;
const BASE_CAPTION_FONT_SIZE = 48; // Base font size at reference width
const BASE_CAPTION_PADDING_V = 12; // Vertical padding at reference width
const BASE_CAPTION_PADDING_H = 24; // Horizontal padding at reference width
const BASE_CAPTION_BOTTOM = 80; // Bottom offset at reference width
const BASE_HIGHLIGHT_SHADOW = 6; // Highlight box-shadow extension at reference width
const BASE_BORDER_RADIUS = 8; // Border radius at reference width

const Captions: FC = () => {
  // Video ref and state
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [captionScale, setCaptionScale] = useState(1);

  // Accordion states
  const [masterTemplateOpen, setMasterTemplateOpen] = useState(true);

  // Load saved settings from localStorage on mount
  const savedSettings = loadMasterTemplateSettings();

  // Options states
  const [optionMode, setOptionMode] = useState<OptionMode>(
    (savedSettings?.optionMode as OptionMode) || 'default',
  );
  const [animationSpeed, setAnimationSpeed] = useState<AnimationSpeed>(
    (savedSettings?.animationSpeed as AnimationSpeed) || 'Instant',
  );
  const [captionFont, setCaptionFont] = useState<FontOption>(
    (savedSettings?.captionFont as FontOption) || 'Montserrat',
  );
  const [captionColor, setCaptionColor] = useState(savedSettings?.captionColor || '#FFFFFF');

  // Border states
  const [borderEnabled, setBorderEnabled] = useState(savedSettings?.borderEnabled ?? false);
  const [borderColor, setBorderColor] = useState(savedSettings?.borderColor || '#000000');
  const [borderWidth, setBorderWidth] = useState<BorderWidth>(
    (savedSettings?.borderWidth as BorderWidth) || '8px',
  );

  // Highlight states
  const [highlightEnabled, setHighlightEnabled] = useState(
    savedSettings?.highlightEnabled ?? false,
  );
  const [highlightColor, setHighlightColor] = useState(savedSettings?.highlightColor || '#FFFF00');
  const [highlightWordColor, setHighlightWordColor] = useState(
    savedSettings?.highlightWordColor || '#FF0000',
  );

  // Adaptive contrast state
  const [adaptiveContrast, setAdaptiveContrast] = useState(
    savedSettings?.adaptiveContrast ?? false,
  );
  const [adaptiveBorderColor, setAdaptiveBorderColor] = useState('#000000');
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Handle mutual exclusivity between adaptive contrast and border
  const handleBorderEnabledChange = (enabled: boolean): void => {
    if (enabled) {
      setBorderEnabled(true);
      setAdaptiveContrast(false);
    } else {
      setBorderEnabled(false);
    }
  };

  const handleAdaptiveContrastChange = (enabled: boolean): void => {
    if (enabled) {
      setAdaptiveContrast(true);
      setBorderEnabled(false);
    } else {
      setAdaptiveContrast(false);
    }
  };

  const animationSpeeds: AnimationSpeed[] = ['0.5x', '1x', '2x', 'Instant'];
  const fonts: FontOption[] = [
    'Montserrat',
    'Open Sans Light',
    'Open Sans Regular',
    'Open Sans Medium',
    'Open Sans SemiBold',
    'Open Sans Bold',
    'Open Sans ExtraBold',
  ];
  const widthOptions: BorderWidth[] = ['4px', '6px', '8px'];

  // Save settings to localStorage whenever they change
  useEffect(() => {
    saveMasterTemplateSettings({
      enabled: true,
      optionMode,
      animationSpeed,
      captionFont,
      captionColor,
      borderEnabled,
      borderColor,
      borderWidth,
      highlightEnabled,
      highlightColor,
      highlightWordColor,
      adaptiveContrast,
    });
  }, [
    optionMode,
    animationSpeed,
    captionFont,
    captionColor,
    borderEnabled,
    borderColor,
    borderWidth,
    highlightEnabled,
    highlightColor,
    highlightWordColor,
    adaptiveContrast,
  ]);

  // Analyze video brightness in the bottom 25% for adaptive contrast
  useEffect(() => {
    if (!adaptiveContrast) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return;

    let animationId: number | null = null;

    const analyzeFrame = (): void => {
      if (video.paused || video.ended) {
        animationId = requestAnimationFrame(analyzeFrame);
        return;
      }

      // Set canvas size to a small sample size for performance
      const sampleWidth = 160;
      const sampleHeight = 90;
      canvas.width = sampleWidth;
      canvas.height = sampleHeight;

      // Draw the current video frame
      ctx.drawImage(video, 0, 0, sampleWidth, sampleHeight);

      // Get the bottom 25% of the video
      const bottomStartY = Math.floor(sampleHeight * 0.75);
      const bottomHeight = sampleHeight - bottomStartY;

      // Get pixel data from the bottom 25%
      let imageData;
      try {
        imageData = ctx.getImageData(0, bottomStartY, sampleWidth, bottomHeight);
      } catch (e) {
        // Canvas tainted by cross-origin data - skip analysis
        animationId = requestAnimationFrame(analyzeFrame);
        return;
      }
      const pixels = imageData.data;

      // Calculate average brightness (using luminance formula)
      let totalBrightness = 0;
      const pixelCount = pixels.length / 4;

      for (let i = 0; i < pixels.length; i += 4) {
        const r = pixels[i];
        const g = pixels[i + 1];
        const b = pixels[i + 2];
        // Use relative luminance formula
        const brightness = 0.299 * r + 0.587 * g + 0.114 * b;
        totalBrightness += brightness;
      }

      const avgBrightness = totalBrightness / pixelCount;

      // Threshold: 128 is mid-point (0-255 range)
      // If dark (brightness < 128), use white border; otherwise, use black border
      const newBorderColor = avgBrightness < 128 ? '#FFFFFF' : '#000000';
      setAdaptiveBorderColor(newBorderColor);

      animationId = requestAnimationFrame(analyzeFrame);
    };

    // Start analyzing when video is playing
    const handlePlay = (): void => {
      analyzeFrame();
    };

    const handlePause = (): void => {
      if (animationId !== null) {
        cancelAnimationFrame(animationId);
        animationId = null;
      }
    };

    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('ended', handlePause);

    // Start analysis if video is already playing
    if (!video.paused) {
      analyzeFrame();
    }

    return () => {
      if (animationId !== null) {
        cancelAnimationFrame(animationId);
      }
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('ended', handlePause);
    };
  }, [adaptiveContrast]);

  // Parse highlight data
  const wordItems = useMemo(() => {
    return (highlightData as HighlightData).items;
  }, []);

  // Get current highlighted word based on video time
  const getCurrentHighlightedWord = useCallback(
    (time: number): WordItem | null => {
      return wordItems.find((item) => time >= item.start && time <= item.end) || null;
    },
    [wordItems],
  );

  // Group words into caption segments (roughly 3-second intervals or by natural breaks)
  const captionSegments = useMemo(() => {
    const segments: { start: number; end: number; words: WordItem[] }[] = [];
    let currentSegment: { start: number; end: number; words: WordItem[] } | null = null;

    wordItems.forEach((word, index) => {
      if (!currentSegment) {
        currentSegment = { start: word.start, end: word.end, words: [word] };
      } else {
        const timeSinceStart = word.end - currentSegment.start;
        const gapFromPrevious = word.start - currentSegment.end;

        // Start new segment if: >3 seconds, or gap > 1 second
        if (timeSinceStart > 3 || gapFromPrevious > 1) {
          segments.push(currentSegment);
          currentSegment = { start: word.start, end: word.end, words: [word] };
        } else {
          currentSegment.end = word.end;
          currentSegment.words.push(word);
        }
      }
    });

    if (currentSegment) {
      segments.push(currentSegment);
    }

    return segments;
  }, [wordItems]);

  // Get current caption segment based on video time
  const getCurrentSegment = useCallback(
    (time: number) => {
      return captionSegments.find((seg) => time >= seg.start && time <= seg.end + 0.5) || null;
    },
    [captionSegments],
  );

  // Handle video time updates
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleTimeUpdate = (): void => {
      setCurrentTime(video.currentTime);
    };

    video.addEventListener('timeupdate', handleTimeUpdate);
    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate);
    };
  }, []);

  // Track video container size and calculate caption scale factor
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let rafId: number | null = null;

    const updateScale = (): void => {
      // Cancel any pending animation frame to debounce rapid resize events
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      rafId = requestAnimationFrame(() => {
        const containerWidth = container.offsetWidth;
        const scale = containerWidth / REFERENCE_VIDEO_WIDTH;
        setCaptionScale(scale);
        rafId = null;
      });
    };

    // Initial calculation
    updateScale();

    // Use ResizeObserver to track size changes
    const resizeObserver = new ResizeObserver(updateScale);
    resizeObserver.observe(container);

    return () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      resizeObserver.disconnect();
    };
  }, []);

  const getSpeedPosition = (speed: AnimationSpeed): number => {
    const index = animationSpeeds.indexOf(speed);
    return (index / (animationSpeeds.length - 1)) * 100;
  };

  const handleSpeedChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const value = parseFloat(e.target.value);
    const index = Math.round((value / 100) * (animationSpeeds.length - 1));
    setAnimationSpeed(animationSpeeds[index]);
  };

  // Get effective caption settings (use defaults when not in customize mode)
  const effectiveSettings = useMemo(() => {
    if (optionMode === 'default') {
      return {
        font: 'Montserrat' as FontOption,
        color: '#FFFFFF',
        borderEnabled: false,
        borderColor: '#000000',
        borderWidth: '8px' as BorderWidth,
        highlightEnabled: false,
        highlightColor: '#FFFF00',
        highlightWordColor: '#FF0000',
      };
    }
    return {
      font: captionFont,
      color: captionColor,
      borderEnabled,
      borderColor,
      borderWidth,
      highlightEnabled,
      highlightColor,
      highlightWordColor,
    };
  }, [
    optionMode,
    captionFont,
    captionColor,
    borderEnabled,
    borderColor,
    borderWidth,
    highlightEnabled,
    highlightColor,
    highlightWordColor,
  ]);

  // Generate text shadow for border effect (scaled based on container size)
  const getTextShadow = useCallback(() => {
    // When adaptive contrast is enabled, always use 8px border with adaptive color
    if (adaptiveContrast && optionMode === 'customize') {
      const baseWidth = 8 / 2; // 8px border width, divided by 2 for the shadow offset
      const scaledWidth = Math.max(1, Math.round(baseWidth * captionScale));
      const color = adaptiveBorderColor;

      const shadows = [];
      for (let x = -scaledWidth; x <= scaledWidth; x++) {
        for (let y = -scaledWidth; y <= scaledWidth; y++) {
          if (x !== 0 || y !== 0) {
            shadows.push(`${x}px ${y}px 0 ${color}`);
          }
        }
      }
      return shadows.join(', ');
    }

    if (!effectiveSettings.borderEnabled) return 'none';

    // Scale the border width based on caption scale
    const baseWidth = parseInt(effectiveSettings.borderWidth, 10) / 2;
    const scaledWidth = Math.max(1, Math.round(baseWidth * captionScale));
    const color = effectiveSettings.borderColor;

    // Create multiple shadows for a solid outline effect
    const shadows = [];
    for (let x = -scaledWidth; x <= scaledWidth; x++) {
      for (let y = -scaledWidth; y <= scaledWidth; y++) {
        if (x !== 0 || y !== 0) {
          shadows.push(`${x}px ${y}px 0 ${color}`);
        }
      }
    }
    return shadows.join(', ');
  }, [
    adaptiveContrast,
    adaptiveBorderColor,
    optionMode,
    effectiveSettings.borderEnabled,
    effectiveSettings.borderColor,
    effectiveSettings.borderWidth,
    captionScale,
  ]);

  // Render caption text with word-level highlighting
  const renderCaptionText = useCallback(() => {
    const segment = getCurrentSegment(currentTime);
    if (!segment) return null;

    const highlightedWord = effectiveSettings.highlightEnabled
      ? getCurrentHighlightedWord(currentTime)
      : null;

    return (
      <span>
        {segment.words.map((word, index) => {
          const isHighlighted = highlightedWord && word.start === highlightedWord.start;

          // Use box-shadow to extend highlight background without affecting layout
          const wordStyle: React.CSSProperties = isHighlighted
            ? {
                backgroundColor: effectiveSettings.highlightColor,
                color: effectiveSettings.highlightWordColor,
                borderRadius: '3px',
                boxShadow: `4px 0 0 ${effectiveSettings.highlightColor}, -4px 0 0 ${effectiveSettings.highlightColor}`,
              }
            : {};

          // Scale highlight box-shadow based on caption scale
          const scaledShadowSize = Math.round(BASE_HIGHLIGHT_SHADOW * captionScale);
          const scaledWordStyle: React.CSSProperties = isHighlighted
            ? {
                ...wordStyle,
                borderRadius: `${Math.round(3 * captionScale)}px`,
                boxShadow: `${scaledShadowSize}px 0 0 ${effectiveSettings.highlightColor}, -${scaledShadowSize}px 0 0 ${effectiveSettings.highlightColor}`,
              }
            : wordStyle;

          return (
            <React.Fragment key={`${word.start}-${index}`}>
              <span style={scaledWordStyle}>{word.text}</span>
              {index < segment.words.length - 1 ? ' ' : ''}
            </React.Fragment>
          );
        })}
      </span>
    );
  }, [
    currentTime,
    getCurrentSegment,
    getCurrentHighlightedWord,
    effectiveSettings.highlightEnabled,
    effectiveSettings.highlightColor,
    effectiveSettings.highlightWordColor,
  ]);

  // Caption container style with scaled dimensions
  const captionStyle: React.CSSProperties = useMemo(
    () => ({
      fontFamily: fontFamilyMap[effectiveSettings.font],
      fontWeight: fontWeightMap[effectiveSettings.font],
      color: effectiveSettings.color,
      textShadow: getTextShadow(),
      // Scale font size and padding based on video container width
      fontSize: `${Math.round(BASE_CAPTION_FONT_SIZE * captionScale)}px`,
      padding: `${Math.round(BASE_CAPTION_PADDING_V * captionScale)}px ${Math.round(
        BASE_CAPTION_PADDING_H * captionScale,
      )}px`,
      borderRadius: `${Math.round(BASE_BORDER_RADIUS * captionScale)}px`,
      lineHeight: 1.4,
    }),
    [effectiveSettings.font, effectiveSettings.color, getTextShadow, captionScale],
  );

  // Caption overlay style with scaled bottom offset
  const captionOverlayStyle: React.CSSProperties = useMemo(
    () => ({
      bottom: `${Math.round(BASE_CAPTION_BOTTOM * captionScale)}px`,
      padding: `0 ${Math.round(20 * captionScale)}px`,
    }),
    [captionScale],
  );

  return (
    <div className={styles.captions}>
      <h1 className={styles.captionsHeadTitle}>Captions</h1>

      <div className={styles.contentWrapper}>
        {/* Left Panel - Accordion Menus */}
        <div className={styles.leftPanel}>
          {/* Master Template Accordion */}
          <div className={styles.accordion}>
            <button
              className={`${styles.accordionHeader} ${masterTemplateOpen ? styles.open : ''}`}
              onClick={() => setMasterTemplateOpen(!masterTemplateOpen)}
            >
              <span>Master Template</span>
              <span className={styles.accordionIcon}>{masterTemplateOpen ? '−' : '+'}</span>
            </button>
            {masterTemplateOpen && (
              <div className={styles.accordionContent}>
                {/* Option Mode Buttons */}
                <div className={styles.optionModeButtons}>
                  <button
                    className={`${styles.modeButton} ${
                      optionMode === 'default' ? styles.active : ''
                    }`}
                    onClick={() => setOptionMode('default')}
                  >
                    Use Default Options
                  </button>
                  <button
                    className={`${styles.modeButton} ${
                      optionMode === 'customize' ? styles.active : ''
                    }`}
                    onClick={() => setOptionMode('customize')}
                  >
                    Customize Options
                  </button>
                </div>

                {/* Customize Options - Only visible when "Customize Options" is selected */}
                {optionMode === 'customize' && (
                  <div className={styles.customizeOptions}>
                    {/* Animation Speed */}
                    <div className={styles.optionGroup}>
                      <label className={styles.optionLabel}>
                        Caption animation speed
                        <span className={styles.tooltipWrapper}>
                          <span className={styles.questionMark}>?</span>
                          <span className={styles.tooltip}>
                            Controls the speed at which captions appear on screen
                          </span>
                        </span>
                      </label>
                      <div className={styles.speedSlider}>
                        <input
                          type="range"
                          min="0"
                          max="100"
                          value={getSpeedPosition(animationSpeed)}
                          onChange={handleSpeedChange}
                          className={styles.slider}
                        />
                        <div className={styles.speedMarks}>
                          {animationSpeeds.map((speed) => (
                            <span
                              key={speed}
                              className={`${styles.speedMark} ${
                                animationSpeed === speed ? styles.activeMark : ''
                              }`}
                            >
                              {speed}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Caption Font */}
                    <div className={styles.optionGroup}>
                      <label className={styles.optionLabel}>
                        Caption font
                        <span className={styles.tooltipWrapper}>
                          <span className={styles.questionMark}>?</span>
                          <span className={styles.tooltip}>
                            Choose the font style for your captions
                          </span>
                        </span>
                      </label>
                      <select
                        value={captionFont}
                        onChange={(e) => setCaptionFont(e.target.value as FontOption)}
                        className={styles.select}
                      >
                        {fonts.map((font) => (
                          <option key={font} value={font}>
                            {font}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Caption Color */}
                    <div className={styles.optionGroup}>
                      <label className={styles.optionLabel}>
                        Caption color
                        <span className={styles.tooltipWrapper}>
                          <span className={styles.questionMark}>?</span>
                          <span className={styles.tooltip}>
                            Set the text color for your captions
                          </span>
                        </span>
                      </label>
                      <div className={styles.colorPicker}>
                        <input
                          type="color"
                          value={captionColor}
                          onChange={(e) => setCaptionColor(e.target.value)}
                          className={styles.colorInput}
                        />
                        <input
                          type="text"
                          value={captionColor}
                          onChange={(e) => setCaptionColor(e.target.value)}
                          className={styles.colorText}
                          placeholder="#FFFFFF"
                        />
                      </div>
                    </div>

                    {/* Caption Border */}
                    <div
                      className={`${styles.optionGroup} ${adaptiveContrast ? styles.disabled : ''}`}
                    >
                      <label className={styles.optionLabel}>
                        Caption border
                        <span className={styles.tooltipWrapper}>
                          <span className={styles.questionMark}>?</span>
                          <span className={styles.tooltip}>
                            Add an outline border around caption text for better visibility
                            {adaptiveContrast && ' (Disabled when Adaptive Contrast is enabled)'}
                          </span>
                        </span>
                      </label>
                      <div className={styles.checkboxGroup}>
                        <label className={styles.checkbox}>
                          <input
                            type="checkbox"
                            checked={borderEnabled}
                            onChange={(e) => handleBorderEnabledChange(e.target.checked)}
                            disabled={adaptiveContrast}
                          />
                          <span>Enable border</span>
                        </label>
                      </div>
                      <div
                        className={`${styles.subOptions} ${!borderEnabled ? styles.disabled : ''}`}
                      >
                        <div className={styles.subOption}>
                          <label>Border color</label>
                          <div className={styles.colorPicker}>
                            <input
                              type="color"
                              value={borderColor}
                              onChange={(e) => setBorderColor(e.target.value)}
                              className={styles.colorInput}
                              disabled={!borderEnabled}
                            />
                            <input
                              type="text"
                              value={borderColor}
                              onChange={(e) => setBorderColor(e.target.value)}
                              className={styles.colorText}
                              placeholder="#000000"
                              disabled={!borderEnabled}
                            />
                          </div>
                        </div>
                        <div className={styles.subOption}>
                          <label>Border width</label>
                          <select
                            value={borderWidth}
                            onChange={(e) => setBorderWidth(e.target.value as BorderWidth)}
                            className={styles.selectCompact}
                            disabled={!borderEnabled}
                          >
                            {widthOptions.map((width) => (
                              <option key={width} value={width}>
                                {width}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>

                    {/* Caption Highlight */}
                    <div className={styles.optionGroup}>
                      <label className={styles.optionLabel}>
                        Caption highlight
                        <span className={styles.tooltipWrapper}>
                          <span className={styles.questionMark}>?</span>
                          <span className={styles.tooltip}>
                            Highlight specific words or phrases in your captions
                          </span>
                        </span>
                      </label>
                      <div className={styles.checkboxGroup}>
                        <label className={styles.checkbox}>
                          <input
                            type="checkbox"
                            checked={highlightEnabled}
                            onChange={(e) => setHighlightEnabled(e.target.checked)}
                          />
                          <span>Enable highlight</span>
                        </label>
                      </div>
                      <div
                        className={`${styles.subOptions} ${
                          !highlightEnabled ? styles.disabled : ''
                        }`}
                      >
                        <div className={styles.subOption}>
                          <label>Highlight color</label>
                          <div className={styles.colorPicker}>
                            <input
                              type="color"
                              value={highlightColor}
                              onChange={(e) => setHighlightColor(e.target.value)}
                              className={styles.colorInput}
                              disabled={!highlightEnabled}
                            />
                            <input
                              type="text"
                              value={highlightColor}
                              onChange={(e) => setHighlightColor(e.target.value)}
                              className={styles.colorText}
                              placeholder="#FFFF00"
                              disabled={!highlightEnabled}
                            />
                          </div>
                        </div>
                        <div className={styles.subOption}>
                          <label>Highlight word</label>
                          <div className={styles.colorPicker}>
                            <input
                              type="color"
                              value={highlightWordColor}
                              onChange={(e) => setHighlightWordColor(e.target.value)}
                              className={styles.colorInput}
                              disabled={!highlightEnabled}
                            />
                            <input
                              type="text"
                              value={highlightWordColor}
                              onChange={(e) => setHighlightWordColor(e.target.value)}
                              className={styles.colorText}
                              placeholder="#FF0000"
                              disabled={!highlightEnabled}
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Adaptive Contrast */}
                    <div className={styles.optionGroup}>
                      <label className={styles.optionLabel}>
                        Adaptive contrast
                        <span className={styles.tooltipWrapper}>
                          <span className={styles.questionMark}>?</span>
                          <span className={styles.tooltip}>
                            Automatically adjusts border color (black/white) based on video
                            brightness. Border width is fixed at 8px when enabled.
                            {borderEnabled && ' (Enabling this will disable Caption Border)'}
                          </span>
                        </span>
                      </label>
                      <div className={styles.toggleSwitch}>
                        <label className={styles.switch}>
                          <input
                            type="checkbox"
                            checked={adaptiveContrast}
                            onChange={(e) => handleAdaptiveContrastChange(e.target.checked)}
                          />
                          <span className={styles.sliderRound}></span>
                        </label>
                        <span className={styles.toggleLabel}>
                          {adaptiveContrast ? 'On' : 'Off'}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Right Panel - Video Preview */}
        <div className={styles.rightPanel}>
          <div className={styles.videoPreviewSticky}>
            <div className={styles.videoContainer} ref={containerRef}>
              <video ref={videoRef} className={styles.videoPlayer} controls playsInline>
                <source src={sampleVideo} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
              {/* Hidden canvas for video frame analysis */}
              <canvas ref={canvasRef} style={{ display: 'none' }} aria-hidden="true" />
              {/* Custom Caption Overlay - sizes scale with video container */}
              <div className={styles.captionOverlay} style={captionOverlayStyle}>
                <div className={styles.captionText} style={captionStyle}>
                  {renderCaptionText()}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Captions;
